import BrowserUtil from '@/platform/oss/browserUtil'
import {
  fetchCloudProjectListIfConfigured,
  type CloudProjectListItem,
} from '@/features/project-cloud/list'
import { fetchCloudProjectListFromOss } from '@/features/project-cloud/ossList'

export type CloudProjectListLoadResult = {
  items: CloudProjectListItem[]
  configured: boolean
}

/**
 * 加载当前用户可见的云端工程列表。
 *
 * 加载顺序：
 * 1. 优先读取 OSS 工程索引；索引不存在时才扫描真实工程目录并回写索引。
 * 2. 当 OSS 未配置、OSS 为空或 OSS 读取失败时，再尝试读取配置的 HTTP 列表接口。
 *
 * @param uid 当前登录账号 uid，用于强制隔离 OSS rootfolder。
 * @returns 返回工程列表以及当前是否存在至少一种可用的云端列表来源。
 * @throws 当 OSS 已配置但读取失败，且 HTTP 列表也不可用时抛出原始异常。
 */
export async function loadCloudProjectList(uid: number | string): Promise<CloudProjectListLoadResult> {
  const safeUid = String(uid || '').trim()
  if (!safeUid) {
    return { items: [], configured: false }
  }
  const oss = BrowserUtil.getInstance().connect(safeUid)
  const ossConfigured = oss.isConfigured() && Boolean(oss.getRootDir())

  if (ossConfigured) {
    try {
      const ossItems = await fetchCloudProjectListFromOss()
      if (ossItems.length > 0) {
        return {
          items: ossItems,
          configured: true,
        }
      }
    } catch (error) {
      const httpFallback = await fetchCloudProjectListIfConfigured().catch(() => null)
      if (httpFallback && !httpFallback.skipped) {
        return {
          items: httpFallback.items,
          configured: true,
        }
      }
      throw error
    }
  }

  const httpResult = await fetchCloudProjectListIfConfigured()
  if (!httpResult.skipped) {
    return {
      items: httpResult.items,
      configured: true,
    }
  }

  return {
    items: [],
    configured: ossConfigured,
  }
}
